import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, Dropdown, Form } from 'react-bootstrap';
import { AppErrorBoundaryComponent } from '../../../../../components/error-boundary/app-error-boundary.component';
import { getIdClassNameHelper } from '../../../../../components/filters/helpers/get-id-class-name.helper';
import { useFindFilterSelection } from '../../../../../components/filters/hooks/common.hook';
import {
  DRILLTHROUGH_DECOMP_INSIGHTS_MAP,
  DRILLTHROUGH_DECOMP_INSIGHTS_MATCHSTRATEGY_MAP,
  useUpdateDrillthrough,
} from '../../../../../components/filters/hooks/use-filter-drillthrough-selection.hook';
import { LoadingComponent } from '../../../../../components/loading/loading.component';
import { useDataProvider } from '../../../../../hooks/data-provider/data-provider.hook';
import {
  FilterSelectedValue,
  OPERATIONS,
  VALUE_MATCH,
} from '../../../../../hooks/filters/interfaces/filter-selected-value.interface';
import { useAppFilterSelectedValue } from '../../../../../hooks/filters/use-app-filter-selected-value.hook';
import { SortBy } from '../../../../../interfaces/sort-by.interface';
import { IMAGES_REPO } from '../../../../../static-resources';
import { IMAGES_REPO as HOME_IMAGES_REPO } from '../../../../../static-resources/home-image-repo';
import { formatBigNumber } from '../../../../../utils/formatting/number-formatter.helper';
import { useSwitchShowDetails } from '../../../../layout/hooks/switch-panel.hook';
import { CompactViewProgressBarComponent } from '../compact-view-progress-bar.component';
import { COMPACT_VIEW_LEVELS } from '../constants/compact-view-levels.constants';
import './tree.component.scss';

const matchStrategy = {
  operation: OPERATIONS.AND,
  valueMatch: VALUE_MATCH.MULTIPLE,
};
const getFilterValueFromRow = (
  row: Record<string, any>,
  valueFieldName: string,
  labelFieldName: string,
  id: string,
) => {
  return {
    id,
    values: [
      {
        value: row[valueFieldName],
        label: row[labelFieldName],
      },
    ],
    matchStrategy,
  };
};
const getProductHierFilterValue = (level: any, id: string) => {
  return {
    id,
    values: [
      {
        value: level.value,
        label: level.label,
      },
    ],
    matchStrategy: {
      operation: OPERATIONS.AND,
      valueMatch: VALUE_MATCH.SINGLE,
    },
  };
};
export interface ChildrenComponentProps {
  filterSelection: FilterSelectedValue[];
  enabled?: boolean;
  defaultExcludeMode?: boolean;
  sortBy: SortBy[] | undefined;
  selectParentExcludeCallback?(): void;
  selectParentIncludeCallback?(): void;
}
export interface CompactViewListComponentProps extends ChildrenComponentProps {
  level: number;
}
export const getDrilldownFilterSelection = (
  filterSelection: FilterSelectedValue[],
  level: number,
  row?: Record<string, any>,
) => {
  //const totalLevels = COMPACT_VIEW_LEVELS.length - 1
  const upperLevel =
    level > 0 ? COMPACT_VIEW_LEVELS[level - 1] : COMPACT_VIEW_LEVELS[level];
  const levelData = COMPACT_VIEW_LEVELS[level];

  const { valueFieldName, labelFieldName, drilldownFilterId } = upperLevel;
  const { nextLevel, levelFilterId } = levelData;

  if (drilldownFilterId) {
    if (nextLevel) {
      if (row) {
        return filterSelection
          .filter((i) => i.id !== levelFilterId)
          .concat([
            getFilterValueFromRow(
              row,
              valueFieldName,
              labelFieldName,
              drilldownFilterId,
            ),
            getProductHierFilterValue(nextLevel, levelFilterId),
          ]);
      } else {
        return filterSelection
          .filter((i) => i.id !== levelFilterId)
          .concat([getProductHierFilterValue(nextLevel, levelFilterId)]);
      }
    }
  }
  return filterSelection;
};
export const ChildrenComponent = ({
  selectParentExcludeCallback,
  selectParentIncludeCallback,
  filterSelection: filterSel,
  level,
  enabled,
  defaultExcludeMode,
  sortBy,
}: CompactViewListComponentProps) => {
  const levelData = COMPACT_VIEW_LEVELS[level];
  const {
    reportId,
    valueFieldName,
    labelFieldName,
    filterId,
    drilldownFilterId,
    levelFilterId,
    className,
    title,
  } = levelData;

  const findFunc = useCallback(
    (sel: FilterSelectedValue) => {
      if (sel && filterId && sel.id === filterId) {
        return false;
      } else {
        return true;
      }
    },
    [filterId],
  );
  const filterSelection = useFindFilterSelection(filterSel, findFunc);
  const { data, isFetching } = useDataProvider<Record<string, any>>(
    reportId,
    filterSelection,
    sortBy,
    enabled,
  );

  //const { selectedValue, setSelectedValue } = useAppFilterSelectedValue(filterId)
  const [selection, setSelection] = useState<
    Record<string, any>[] | undefined
  >();
  const [excludeMode, setExcludeMode] = useState<boolean | undefined>(
    defaultExcludeMode,
  );

  //const [started, setStarted] = useState<boolean>(false)
  const [defaultExcludeModeChildren, setDefaultExcludeModeChildren] = useState<
    Record<number, boolean>
  >({});

  const { selectedValue, setSelectedValue } =
    useAppFilterSelectedValue(filterId);
  const setFilter = useCallback(
    (element: Record<string, any>, add: boolean) => {
      if (selectedValue) {
        //console.log("SET SELECTED VALUE: ", selectedValue)
        const val = {
          value: element[valueFieldName],
          label: element[labelFieldName],
          exclude: excludeMode,
        };
        const values = selectedValue.values.filter(
          (i) => i.value !== val.value,
        );

        if (add) {
          setSelectedValue({
            ...selectedValue,
            matchStrategy,
            values: values.concat(val),
          });
        } else {
          if (selectedValue.values.length !== values.length) {
            setSelectedValue({ ...selectedValue, matchStrategy, values });
          }
        }
      }
    },
    [
      excludeMode,
      setSelectedValue,
      selectedValue,
      valueFieldName,
      labelFieldName,
    ],
  );

  useEffect(() => {
    if (data) {
      const rows = data.rows;
      setTotalCount(rows.length);
    }
  }, [data]);
  useEffect(() => {
    if (data && selectedValue) {
      const rows = data.rows;
      const values = selectedValue.values;
      const sel = rows.filter(
        (r) => values.find((v) => v.value === r[valueFieldName]) !== undefined,
      );
      if (sel.length === 0) {
        setSelection(undefined);
        setSelCount(null);
      } else {
        setSelection(sel);
        setSelCount(sel.length);
      }
    }
  }, [data, selectedValue, valueFieldName]);
  const clearSelection = useCallback(() => {
    if (data && selectedValue) {
      const rows = data.rows;
      const values = selectedValue.values;
      const filteredValues = values.filter(
        (v) => rows.find((r) => v.value === r[valueFieldName]) === undefined,
      );
      setSelection(undefined);
      setSelCount(null);
      if (filteredValues.length !== values.length) {
        setSelectedValue({
          ...selectedValue,
          matchStrategy,
          values: filteredValues,
        });
      }
    }
  }, [selectedValue, setSelectedValue, data, valueFieldName]);

  useEffect(() => {
    setDefaultExcludeModeChildren({});
    setExcludeMode(defaultExcludeMode);
  }, [defaultExcludeMode]);

  const onSelectCallback = useCallback(
    (
      element: Record<string, any>,
      checked: boolean,
      index: number,
      propagate: boolean,
    ) => {
      const add = (excludeMode && !checked) || (!excludeMode && checked);

      setFilter(element, add);

      if (propagate) {
        setDefaultExcludeModeChildren({
          ...defaultExcludeModeChildren,
          [index]: add,
        });
      }
    },
    [setFilter, excludeMode, defaultExcludeModeChildren],
  );

  const [selCount, setSelCount] = useState<number | null>(null);

  const [totalCount, setTotalCount] = useState<number | null>(null);

  const isChecked = useCallback(
    (row: Record<string, any>) => {
      if (!selection) return excludeMode ? true : false;
      return excludeMode
        ? selection.indexOf(row) === -1
        : selection.indexOf(row) !== -1;
    },
    [selection, excludeMode],
  );

  const getDefaultExcludeMode = useCallback(
    (index: number) => {
      return excludeMode
        ? !defaultExcludeModeChildren[index]
        : defaultExcludeModeChildren[index];
    },
    [defaultExcludeModeChildren, excludeMode],
  );

  const [showChildrenMap, setShowChildrenMap] = useState<
    Record<number, boolean>
  >({});
  const isShown = useCallback(
    (index: number) => {
      return showChildrenMap[index] ?? false;
    },
    [showChildrenMap],
  );

  const toggleShowChildren = useCallback(
    (index: number) => {
      setShowChildrenMap({
        ...showChildrenMap,
        [index]: !showChildrenMap[index],
      });
    },
    [showChildrenMap],
  );

  useEffect(() => {
    if (totalCount !== null && totalCount > 0) {
      if (totalCount === selCount) {
        if (!excludeMode && selectParentIncludeCallback) {
          selectParentIncludeCallback();
        }
        if (excludeMode && selectParentExcludeCallback) {
          selectParentExcludeCallback();
        }
        clearSelection();
        setExcludeMode(!excludeMode);
      }
    }
  }, [
    totalCount,
    selCount,
    selectParentIncludeCallback,
    selectParentExcludeCallback,
    excludeMode,
    clearSelection,
  ]);

  useEffect(() => {
    if (selCount !== null && selCount === 1 && excludeMode === false) {
      if (selectParentIncludeCallback) selectParentIncludeCallback();
    }
  }, [selCount, selectParentIncludeCallback, excludeMode, clearSelection]);

  const handleChildExcludeSelection = useCallback(
    (row: Record<string, any>, index: number) => {
      onSelectCallback(row, false, index, false);
    },
    [onSelectCallback],
  );

  const handleChildIncludeSelection = useCallback(
    (row: Record<string, any>, index: number) => {
      onSelectCallback(row, true, index, false);
    },
    [onSelectCallback],
  );

  const { setShow: setShowDetails } = useSwitchShowDetails('d', false);

  const updateDrillthrough = useUpdateDrillthrough();

  const drillThroughHandler = useCallback(
    (row: Record<string, any>) => {
      if (enabled === true && drilldownFilterId) {
        setShowDetails(true);
        updateDrillthrough(
          filterSelection
            .filter((i) => i.id !== levelFilterId)
            .concat([
              getFilterValueFromRow(
                row,
                valueFieldName,
                labelFieldName,
                drilldownFilterId,
              ),
            ]),
        );
      }
    },
    [
      updateDrillthrough,
      filterSelection,
      levelFilterId,
      valueFieldName,
      labelFieldName,
      drilldownFilterId,
      enabled,
      setShowDetails,
    ],
  );

  const isLastLevel = useMemo(() => {
    return level + 1 === COMPACT_VIEW_LEVELS.length;
  }, [level]);

  const { setShow: setShowDecomp } = useSwitchShowDetails('d', false, '/t/i');

  const updateDrillthroughDecompInsights = useUpdateDrillthrough(
    DRILLTHROUGH_DECOMP_INSIGHTS_MAP,
    DRILLTHROUGH_DECOMP_INSIGHTS_MATCHSTRATEGY_MAP,
  );

  const handleDecomp = useCallback(
    async (row: Record<string, any>) => {
      if (enabled === true && drilldownFilterId) {
        setShowDecomp(true);
        updateDrillthroughDecompInsights(
          filterSelection
            .filter((i) => i.id !== levelFilterId)
            .concat([
              getFilterValueFromRow(
                row,
                valueFieldName,
                labelFieldName,
                drilldownFilterId,
              ),
            ]),
        );
      }
    },
    [
      enabled,
      drilldownFilterId,
      setShowDecomp,
      updateDrillthroughDecompInsights,
      filterSelection,
      valueFieldName,
      labelFieldName,
      levelFilterId,
    ],
  );

  return (
    <LoadingComponent
      isLoading={isFetching || !enabled}
      message={`Loading ${title}...`}
    >
      <div className="compact-view-wrapper">
        {data?.rows.map((row, index) => (
          <div
            key={index}
            className={`${getIdClassNameHelper(row[valueFieldName])} ${className}`}
          >
            <div
              className={
                isShown(index)
                  ? 'sidebar1_accordion active'
                  : 'sidebar1_accordion d'
              }
            >
              <div className="cv-accordian">
                <div className="container-fluid">
                  <div className="row align-items-center">
                    <div className="col-lg-1">
                      <div className="row align-items-center font-14">
                        <div className="col-6">
                          <Form.Check
                            key={excludeMode ? 'excl' : 'incl'}
                            className="compactoggleswitch"
                            label=""
                            checked={isChecked(row)}
                            onChange={(e) =>
                              onSelectCallback(
                                row,
                                e?.currentTarget?.checked,
                                index,
                                true,
                              )
                            }
                            type={isLastLevel ? 'checkbox' : 'switch'}
                          />
                        </div>

                        <div className="col-6">
                          {!isLastLevel && (
                            <Button
                              onClick={() => toggleShowChildren(index)}
                              variant=""
                              size="sm"
                            >
                              <img
                                className={
                                  isShown(index)
                                    ? 'compact-view-button close'
                                    : 'compact-view-button open'
                                }
                                src={IMAGES_REPO.SimpleArrow}
                                loading="lazy"
                                alt=""
                                height={18}
                                width={18}
                              />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col font-14">
                      <div className="row align-items-center">
                        {data.columns.map((col) => (
                          <div
                            key={`${col.id}-${row[col.id]?.toString()}`}
                            className="col ml-8"
                          >
                            {row[col.id]?.toString()}
                          </div>
                        ))}

                        <div className="col-6 font-14">
                          <CompactViewProgressBarComponent
                            min={0}
                            max={data.max}
                            value={row[data.measureColumns[0].id]}
                          />
                        </div>
                        {data?.measureColumns.map((col) => (
                          <div
                            key={`${col.id}-${row[col.id]?.toString()}`}
                            className="col text-end"
                          >
                            {formatBigNumber(row[col.id])}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col cv-accordian-action font-14 text-end">
                      <Dropdown
                        id="reports-button"
                        title="Reports"
                        as={ButtonGroup}
                      >
                        <Dropdown.Toggle
                          variant="sm"
                          className="reports-navigation-button"
                          id="reports-button-toggle"
                        >
                          <img src={HOME_IMAGES_REPO.DemandButtonIcon} alt="" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu align={'end'}>
                          <Dropdown.Item
                            onClick={() => drillThroughHandler(row)}
                          >
                            Drill Through
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleDecomp(row)}>
                            Decomp
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              </div>
              {isShown(index) && !isLastLevel && (
                <AppErrorBoundaryComponent>
                  <ChildrenComponent
                    key={
                      excludeMode
                        ? `excludeMode${index}`
                        : `includeMode${index}`
                    }
                    filterSelection={getDrilldownFilterSelection(
                      filterSel,
                      level + 1,
                      row,
                    )}
                    level={level + 1}
                    enabled={enabled}
                    sortBy={sortBy}
                    //selectParentCallback={(checked) => handleChildSelectionCallback(row, index)(checked)}
                    selectParentExcludeCallback={() =>
                      handleChildExcludeSelection(row, index)
                    }
                    selectParentIncludeCallback={() =>
                      handleChildIncludeSelection(row, index)
                    }
                    defaultExcludeMode={
                      getDefaultExcludeMode(index) && isChecked(row)
                    }
                  />
                </AppErrorBoundaryComponent>
              )}
            </div>
          </div>
        ))}
      </div>
    </LoadingComponent>
  );
};
